import { useState } from 'react';
import { X, Check } from 'lucide-react';

interface DeleteAccountModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (password: string, reason: string) => Promise<void>;
}

export default function DeleteAccountModal({ isOpen, onClose, onConfirm }: DeleteAccountModalProps) {
    const [step, setStep] = useState(1);
    const [reason, setReason] = useState<string>('');
    const [otherReason, setOtherReason] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    if (!isOpen) return null;

    const handleNext = () => {
        if (step === 2 && !reason) {
            setError('Please select a reason');
            return;
        }
        setError(null);
        setStep(step + 1);
    };

    const handleSubmit = async () => {
        if (!password) {
            setError('Please enter your password');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const finalReason = reason === 'Other' ? otherReason : reason;
            await onConfirm(password, finalReason);
            // Modal will be closed by parent usually, or we can close it here if successful?
            // Parent handles navigation/logout usually.
        } catch (err: any) {
            setError(err.message || 'Failed to delete account');
            setLoading(false);
        }
    };

    const handleBack = () => {
        onClose(); // In this flow, "Cancel" or "Keep Subscription" usually closes the modal
    };

    // Reusable styles
    const overlayStyle = "fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4";
    const modalStyle = "bg-white rounded-[20px] w-full max-w-[600px] p-6 md:p-8 relative shadow-xl transform transition-all max-h-[90vh] overflow-y-auto";
    const titleStyle = "text-xl font-medium text-gray-900 mb-2";
    const descStyle = "text-gray-500 text-[15px] mb-8 leading-relaxed";

    // "Keep" button (Primary action to stay) - Blue
    const keepBtnStyle = "px-6 py-2.5 bg-[#1976D3] text-white rounded-lg font-medium hover:bg-blue-700 transition-colors";

    // "Continue" button (Destructive action) - White Outline
    const continueBtnStyle = "px-6 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors";

    return (
        <div className={overlayStyle} style={{ fontFamily: 'Montserrat, sans-serif' }}>
            <div className={modalStyle}>

                {/* Step 1: Initial Confirmation */}
                {step === 1 && (
                    <div className="text-left">
                        <h2 className={titleStyle}>Are you sure you want to Delete your account?</h2>
                        <p className={descStyle}>
                            Deleting your account will remove all your data and you will not be
                            able to recover it.
                        </p>

                        <div className="flex justify-end gap-3 mt-8">
                            <button onClick={onClose} className={keepBtnStyle}>
                                Cancel
                            </button>
                            <button onClick={handleNext} className={continueBtnStyle}>
                                Continue
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 2: Reason Survey */}
                {step === 2 && (
                    <div className="text-left">
                        <h2 className={titleStyle}>We're Sorry to see you go</h2>
                        <p className={descStyle}>
                            Before you go, could you tell us why you're leaving? Your feedback
                            genuinely helps us make Conncct better.
                        </p>

                        <h3 className="text-[16px] font-medium text-gray-900 mb-4">Why are you canceling your subscription?</h3>
                        <p className="text-gray-400 text-xs mb-4">If there's something we could fix or improve before you leave, we'd love to know.</p>

                        <div className="space-y-3 mb-8">
                            {[
                                "Too expensive",
                                "Missing features I need",
                                "Technical Issues",
                                "Not sure how to use the product",
                                "No longer need this service",
                                "Didn't meet my needs",
                                "Company is shutting down",
                                "Other"
                            ].map((r) => (
                                <label key={r} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors group">
                                    <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${reason === r ? 'border-[#1976D3] bg-[#1976D3]' : 'border-gray-400 bg-white'}`}>
                                        {reason === r && <Check className="w-3 h-3 text-white" />}
                                    </div>
                                    <input
                                        type="radio"
                                        name="reason"
                                        value={r}
                                        checked={reason === r}
                                        onChange={(e) => setReason(e.target.value)}
                                        className="hidden"
                                    />
                                    <span className="text-sm text-gray-700 font-medium">{r}</span>
                                </label>
                            ))}

                            {reason === 'Other' && (
                                <textarea
                                    className="w-full mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    placeholder="Please tell us more..."
                                    rows={3}
                                    value={otherReason}
                                    onChange={(e) => setOtherReason(e.target.value)}
                                />
                            )}
                        </div>

                        {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

                        <div className="flex justify-end gap-3">
                            <button onClick={onClose} className={keepBtnStyle}>
                                Keep Subscription
                            </button>
                            <button onClick={handleNext} className={continueBtnStyle}>
                                Continue to Cancel
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 3: Password Confirmation */}
                {step === 3 && (
                    <div className="text-left">
                        <h2 className={titleStyle}>We're Sorry to see you go</h2>
                        <p className={descStyle}>
                            Before you cancel could you please share why you're leaving? Your
                            feedback helps us improve!
                        </p>

                        <h3 className="text-[16px] font-medium text-gray-900 mb-4">One last question, any specific feedback you'd like to share?</h3>

                        <div className="mb-8">
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter Your Password"
                                className="w-full p-4 border border-gray-400 rounded-lg text-black bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all font-medium"
                            />
                        </div>

                        {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

                        <div className="flex justify-end gap-3">
                            <button onClick={onClose} className={keepBtnStyle}>
                                Keep Subscription
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={loading}
                                className={`${continueBtnStyle} text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                                {loading ? 'Deleting...' : 'Continue to Cancel'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
